import { useRef } from 'react'
import ReactPlayer from 'react-player'

type Stats = {
    milesLogged: string
    participants: string
    caloriesBurned: string
}

type Metadata = {
    spotifyPlaylist?: string
    participateFrom?: string
    completeBy?: string
    finish?: string
    suitableFor?: string
}

interface RaceDetailContentProps {
    htmlString: string
    stats: Stats
    metadata: Metadata
    video?: {
        videoUrl: string
        videoThumbUrl: string
    }
    isHowFarCanYouGo: boolean
}

export const RaceDetailContent = ({
    htmlString = '',
    stats,
    video = null,
    isHowFarCanYouGo = null
}: RaceDetailContentProps) => {
    const videoRef: any = useRef(null)
    return (
        <div>
            <div className="grid gap-x-5 grid-cols-1 xl:grid-cols-3 p-5 pt-0 sm:pb-10 md:pt-5 mb-2">
                <section className="md:col-span-2 md:mt-5">
                    <div
                        className="prose max-w-4xl mt-5"
                        dangerouslySetInnerHTML={{ __html: htmlString }}
                    />
                    {video !== null ? (
                        <div className="max-w-[800px] mt-3 md:mt-5">
                            <div className="aspect-w-16 aspect-h-9 rounded-lg">
                                <ReactPlayer
                                    ref={videoRef}
                                    playIcon={
                                        <i className="fas fa-play text-5xl md:text-7xltext-white z-10"></i>
                                    }
                                    playing={true}
                                    className="react-player"
                                    style={{ borderRadius: 20, overflow: 'hidden' }}
                                    width="100%"
                                    height="100%"
                                    url={video?.videoUrl}
                                    light={video?.videoThumbUrl}
                                    controls
                                    onEnded={() => {
                                        videoRef?.current?.showPreview()
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </section>
                <section>
                    <div className="bg-white mt-5 drop-shadow-md rounded-lg p-2 sm:p-7 max-w-full md:max-w-xl">
                        <p className="mb-3 font-normal text-xl lg:text-3xl pl-3 sm:pl-0">
                            Stats for this Race
                        </p>
                        <div className="h-[1px] bg-gray-100 w-full mb-4" />
                        <div className="flex">
                            <div className="w-1/3 relative flex flex-col items-center mx-1">
                                <div className="relative border-4 border-solid border-yf-green rounded-full w-20 h-20 lg:w-28 lg:h-28 mb-2 flex items-center justify-center text-center">
                                    <i className="fas fa-running text-4xl lg:text-5xl absolute text-yf-green opacity-40"></i>
                                    <p className="z-10 font-medium lg:text-xl">
                                        {stats.milesLogged}
                                    </p>
                                </div>
                                <p className="text-xs md:text-sm xlg:text-base lg:leading-4">
                                    Miles Logged
                                </p>
                            </div>
                            <div className="w-1/3 relative flex flex-col items-center mx-1">
                                <div className="border-4 border-solid border-4 border-yf-purple rounded-full w-20 h-20 lg:w-28 lg:h-28 mb-2 flex items-center justify-center text-center">
                                    <i className="fas fa-users text-4xl lg:text-5xl absolute text-yf-purple opacity-40"></i>
                                    <p className="z-10 font-medium lg:text-xl">
                                        {stats.participants}
                                    </p>
                                </div>
                                <p className="text-xs md:text-sm xlg:text-base text-center lg:leading-4">
                                    Race Participants
                                </p>
                            </div>
                            <div className="w-1/3 relative flex flex-col items-center mx-1">
                                <div className="border-4 border-solid border-blue-600 rounded-full w-20 h-20 lg:w-28 lg:h-28 mb-2 flex items-center justify-center">
                                    <i className="fas fa-fire text-4xl lg:text-5xl absolute text-blue-600 opacity-40"></i>
                                    <p className="z-10 font-medium lg:text-xl">
                                        {stats.caloriesBurned}
                                    </p>
                                </div>
                                <p className="text-xs md:text-sm xlg:text-base text-center lg:leading-4">
                                    Calories Burned
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {isHowFarCanYouGo && (
                <section className="mt-5 py-10 bg-white sm:py-16 lg:py-24">
                    <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                        <div className="max-w-2xl mx-auto text-center">
                            <h2 className="text-3xl font-bold leading-tight text-yf-green sm:text-4xl lg:text-5xl">
                                This is a How Far Can You Go Event
                            </h2>
                            <p className="max-w-lg mx-auto mt-4 text-base leading-relaxed text-gray-600">
                                It is not designed for you to finish but to keep you
                                motivated and engaged for the entire year. Here is how it
                                works:
                            </p>
                        </div>

                        <div className="relative mt-12 lg:mt-20">
                            <div className="relative grid grid-cols-1 text-center gap-y-12 md:grid-cols-4 gap-x-12">
                                <div>
                                    <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
                                        <span className="text-xl font-semibold text-yf-purple">
                                            {' '}
                                            1{' '}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
                                        Go At Your Own Pace
                                    </h3>
                                    <p className="mt-4 text-base text-gray-600">
                                        Set this event up for daily activities or multiple
                                        forms of tracking (walking, cycling, running etc).
                                    </p>
                                </div>

                                <div>
                                    <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
                                        <span className="text-xl font-semibold text-yf-purple">
                                            {' '}
                                            2{' '}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
                                        Take Part in other events on the Yes.Fit platform
                                    </h3>
                                    <p className="mt-4 text-base text-gray-600">
                                        You can &quot;participate&quot; in other events
                                        while in a HFCYG event.
                                    </p>
                                </div>

                                <div>
                                    <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
                                        <span className="text-xl font-semibold text-yf-purple">
                                            {' '}
                                            3{' '}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
                                        Track Your Progress
                                    </h3>
                                    <p className="mt-4 text-base text-gray-600">
                                        Connect your tracker, or watch like and we will do
                                        the rest. Come back and periodically view where
                                        you are on the course. Join the group in the app
                                        and brag about how far you have gone.
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
                                        <span className="text-xl font-semibold text-yf-purple">
                                            {' '}
                                            4{' '}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
                                        Special Events Galore
                                    </h3>
                                    <p className="mt-4 text-base text-gray-600">
                                        HFCYG events are special events and we often have
                                        additional contest and prizes throughout the year
                                        to keep you motivated and logging miles. Follow
                                        the group in the app to learn more.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
}
